/**
 * The exam wrapper's strings (pedagogy spec §7.4), kept in their own block so
 * the feature dictionary in `../index.ts` merges them with one spread. Course
 * chapters are "moment" / "module" (Y&F §13 #2).
 */
import type { LocalizedDictionary } from "@client/i18n";

export const wrapperDict: LocalizedDictionary = {
  sv: {
    "exam.result.toVG.one": "{points} p kvar till VG",
    "exam.result.toVG.other": "{points} p kvar till VG",
    "exam.result.toG.one": "{points} p kvar till G utan bonuspoäng",
    "exam.result.toG.other": "{points} p kvar till G utan bonuspoäng",
    "exam.wrapper.causes.title": "Var tappade du poängen?",
    "exam.wrapper.causes.description":
      "Välj för varje uppgift vad som kostade poäng. Du kan välja flera orsaker.",
    "exam.wrapper.causes.legend": "Uppgift {slot} · {score} av {max} p · {chapter}",
    "exam.wrapper.chapter": "moment {chapter}",
    "exam.wrapper.causes.fullMarks.one": "Full poäng på uppgift {list}",
    "exam.wrapper.causes.fullMarks.other": "Full poäng på uppgift {list}",
    "exam.wrapper.causes.none":
      "Du tappade inga poäng på den här övningstentan. Skriv gärna en plan ändå.",
    "exam.wrapper.causes.blank": "Inget svar inskrivet",
    "exam.wrapper.cause.recognise": "Kände inte igen uppgiftstypen",
    "exam.wrapper.cause.principle": "Valde fel princip",
    "exam.wrapper.cause.concept": "Kunde inte teorin bakom",
    "exam.wrapper.cause.setup": "Uppställning eller modell",
    "exam.wrapper.cause.algebra": "Algebra eller räkning",
    "exam.wrapper.cause.units": "Enheter eller tiopotenser",
    "exam.wrapper.cause.explain": "Motivering eller förklaring saknades",
    "exam.wrapper.cause.time": "Tiden räckte inte",
    "exam.wrapper.cause.unknown": "Vet inte",
    "exam.wrapper.pattern.title": "Ditt mönster",
    "exam.wrapper.pattern.description":
      "Tappade poäng per orsak. Har en uppgift flera orsaker delas poängen lika.",
    "exam.wrapper.group.principle": "Känna igen uppgiften eller välja princip",
    "exam.wrapper.group.setup": "Uppställning, teori eller motivering",
    "exam.wrapper.group.unassigned": "Ingen orsak vald än",
    "exam.wrapper.pattern.beforeCalc.one":
      "Av de {assigned} poäng där du har valt orsak tappade du {points} innan du hade börjat räkna: när du skulle känna igen uppgiften, välja princip, kunna teorin bakom eller ställa upp lösningen.",
    "exam.wrapper.pattern.beforeCalc.other":
      "Av de {assigned} poäng där du har valt orsak tappade du {points} innan du hade börjat räkna: när du skulle känna igen uppgiften, välja princip, kunna teorin bakom eller ställa upp lösningen.",
    "exam.wrapper.pattern.empty": "Välj orsaker för uppgifterna så visas ditt mönster här.",
    "exam.wrapper.pattern.onlyUnknown":
      "Du har valt ”Vet inte” för alla uppgifter, så här finns inget mönster att visa.",
    "exam.wrapper.next.title": "Förslag på nästa steg",
    "exam.wrapper.next.description": "Förslagen utgår från orsakerna du har valt.",
    "exam.wrapper.next.none": "Välj orsaker så får du förslag på vad du kan öva på.",
    "exam.wrapper.next.onlyUnknown":
      "Du har valt ”Vet inte” för alla uppgifter, så det finns inget förslag att ge än.",
    "exam.wrapper.next.whichPrinciple.title": "Vilken princip?",
    "exam.wrapper.next.whichPrinciple.body.one":
      "{count} uppgift från {chapters}, bara planeringssteget (ca {minutes} min)",
    "exam.wrapper.next.whichPrinciple.body.other":
      "{count} blandade uppgifter från {chapters}, bara planeringssteget (ca {minutes} min)",
    "exam.wrapper.chapters.one": "moment {list}",
    "exam.wrapper.chapters.other": "moment {list}",
    "exam.wrapper.next.timedPair.title": "Två uppgifter på tid",
    "exam.wrapper.next.timedPair.body":
      "Två tentauppgifter på sammanlagt {total} poäng, på {minutes} min. Tiden följer takten på din övningstenta.",
    "exam.wrapper.next.setup.title": "Öva uppställning",
    "exam.wrapper.next.setup.body":
      "Öppna uppgifter i {topics}. Skriv upp modellen och sambanden innan du räknar, och motivera varför de gäller.",
    "exam.wrapper.next.accuracy.title": "Räkna noggrant",
    "exam.wrapper.next.accuracy.body":
      "Numeriska uppgifter i {topics}. Kontrollera enheter och tiopotenser innan du svarar.",
    "exam.wrapper.next.badge.interleaved": "Blandat",
    "exam.wrapper.next.badge.pacing": "Tempo",
    "exam.wrapper.next.badge.setup": "Uppställning",
    "exam.wrapper.next.badge.accuracy": "Noggrannhet",
    "exam.wrapper.next.start": "Starta",
    "exam.wrapper.next.addToToday": "Lägg till i dagens pass",
    "exam.wrapper.next.added": "Tillagt i dagens pass",
    "exam.wrapper.plan.label": "Inför nästa övningstenta ska jag …",
    "exam.wrapper.plan.placeholder":
      "t.ex. läsa igenom alla uppgifter först och börja med dem jag känner igen",
    "exam.wrapper.plan.note": "Vi visar den här raden när du startar din nästa övningstenta.",
    "exam.wrapper.plan.labelOlder": "Din plan från den här övningstentan",
    "exam.wrapper.plan.noteOlder":
      "Nästa gång du startar en övningstenta visar vi planen från den senaste.",
    "exam.wrapper.plan.count": "{used} av {max} tecken",
    "exam.wrapper.save": "Spara reflektionen",
    "exam.wrapper.private": "Bara du ser dina orsaker och din plan. Lärare ser dem inte.",
    "exam.lastPlan.title": "Din plan från förra övningstentan",
    "exam.lastPlan.written": "Skriven {date}",
    "exam.lastPlan.hide": "Dölj",
  },
  en: {
    "exam.result.toVG.one": "{points} point from VG",
    "exam.result.toVG.other": "{points} points from VG",
    "exam.result.toG.one": "{points} point from G without bonus points",
    "exam.result.toG.other": "{points} points from G without bonus points",
    "exam.wrapper.causes.title": "Where did the points go?",
    "exam.wrapper.causes.description":
      "For each problem, choose what cost you points. You can choose more than one cause.",
    "exam.wrapper.causes.legend": "Problem {slot} · {score} of {max} p · {chapter}",
    "exam.wrapper.chapter": "module {chapter}",
    "exam.wrapper.causes.fullMarks.one": "Full marks on problem {list}",
    "exam.wrapper.causes.fullMarks.other": "Full marks on problems {list}",
    "exam.wrapper.causes.none": "You lost no points on this exam. You can still write a plan.",
    "exam.wrapper.causes.blank": "No answer entered",
    "exam.wrapper.cause.recognise": "Did not recognise the problem type",
    "exam.wrapper.cause.principle": "Chose the wrong principle",
    "exam.wrapper.cause.concept": "Did not know the theory behind it",
    "exam.wrapper.cause.setup": "Set-up or model",
    "exam.wrapper.cause.algebra": "Algebra or arithmetic",
    "exam.wrapper.cause.units": "Units or powers of ten",
    "exam.wrapper.cause.explain": "Justification or explanation missing",
    "exam.wrapper.cause.time": "Ran out of time",
    "exam.wrapper.cause.unknown": "Don't know",
    "exam.wrapper.pattern.title": "Your pattern",
    "exam.wrapper.pattern.description":
      "Points lost by cause. When a problem has several causes, its points are split equally.",
    "exam.wrapper.group.principle": "Recognising the problem or choosing the principle",
    "exam.wrapper.group.setup": "Set-up, theory or justification",
    "exam.wrapper.group.unassigned": "No cause chosen yet",
    "exam.wrapper.pattern.beforeCalc.one":
      "Of the {assigned} point you gave a cause for, {points} was lost before any calculation: in recognising the problem, choosing the principle, knowing the theory behind it or setting up the solution.",
    "exam.wrapper.pattern.beforeCalc.other":
      "Of the {assigned} points you gave a cause for, {points} were lost before any calculation: in recognising the problem, choosing the principle, knowing the theory behind it or setting up the solution.",
    "exam.wrapper.pattern.empty": "Choose causes for the problems and your pattern appears here.",
    "exam.wrapper.pattern.onlyUnknown":
      "You chose “Don't know” for every problem, so there is no pattern to show.",
    "exam.wrapper.next.title": "Suggested next",
    "exam.wrapper.next.description": "The suggestions follow from the causes you chose.",
    "exam.wrapper.next.none": "Choose causes to get suggestions for what to practise.",
    "exam.wrapper.next.onlyUnknown":
      "You chose “Don't know” for every problem, so there is no suggestion to give yet.",
    "exam.wrapper.next.whichPrinciple.title": "Which principle?",
    "exam.wrapper.next.whichPrinciple.body.one":
      "{count} problem from {chapters}, plan step only (about {minutes} min)",
    "exam.wrapper.next.whichPrinciple.body.other":
      "{count} mixed problems from {chapters}, plan step only (about {minutes} min)",
    "exam.wrapper.chapters.one": "module {list}",
    "exam.wrapper.chapters.other": "modules {list}",
    "exam.wrapper.next.timedPair.title": "Timed pair",
    "exam.wrapper.next.timedPair.body":
      "Two exam-style problems worth {total} points in all, in {minutes} min. The time follows the pace of your exam.",
    "exam.wrapper.next.setup.title": "Practise set-up",
    "exam.wrapper.next.setup.body":
      "Open problems in {topics}. Write down the model and the relations before you calculate, and justify why they hold.",
    "exam.wrapper.next.accuracy.title": "Careful calculation",
    "exam.wrapper.next.accuracy.body":
      "Numeric problems in {topics}. Check units and powers of ten before you answer.",
    "exam.wrapper.next.badge.interleaved": "Interleaved",
    "exam.wrapper.next.badge.pacing": "Pacing",
    "exam.wrapper.next.badge.setup": "Set-up",
    "exam.wrapper.next.badge.accuracy": "Accuracy",
    "exam.wrapper.next.start": "Start",
    "exam.wrapper.next.addToToday": "Add to today's session",
    "exam.wrapper.next.added": "Added to today's session",
    "exam.wrapper.plan.label": "Before my next exam I will …",
    "exam.wrapper.plan.placeholder":
      "e.g. read all the problems first and start with the ones I recognise",
    "exam.wrapper.plan.note": "We will show you this line when you start your next exam.",
    "exam.wrapper.plan.labelOlder": "Your plan from this exam",
    "exam.wrapper.plan.noteOlder": "At your next exam we show the plan from your most recent exam.",
    "exam.wrapper.plan.count": "{used} of {max} characters",
    "exam.wrapper.save": "Save reflection",
    "exam.wrapper.private": "Only you see your causes and your plan. Teachers do not.",
    "exam.lastPlan.title": "Your plan from your last exam",
    "exam.lastPlan.written": "Written {date}",
    "exam.lastPlan.hide": "Hide",
  },
};
